import { inject } from '@angular/core';
import { Routes } from '@angular/router';

import { GISGO_AUTH_CONTEXT, staffRoleGuard } from '@gisgo/shared';

import { authGuard } from './core/auth/auth.guard';
import { Shell } from './core/layout/shell';

export const routes: Routes = [
  {
    path: 'login',
    title: 'Connexion — Gisgo Ops',
    loadComponent: () => import('./features/auth/login-page').then((m) => m.LoginPage),
  },
  {
    path: '',
    component: Shell,
    canActivate: [authGuard],
    children: [
      {
        path: '',
        pathMatch: 'full',
        // Atterrissage par rôle : sales → pipeline, sinon console
        redirectTo: () =>
          inject(GISGO_AUTH_CONTEXT).role() === 'sales'
            ? 'commercial/pipeline'
            : 'supervision/console',
      },
      {
        path: 'supervision/console',
        title: 'Console dispatch',
        data: { fullBleed: true },
        canActivate: [staffRoleGuard('dispatcher')],
        loadComponent: () =>
          import('./features/supervision/console/console-page').then((m) => m.ConsolePage),
      },
      {
        path: 'supervision/flotte',
        title: 'Suivi flotte',
        data: { fullBleed: true },
        canActivate: [staffRoleGuard('dispatcher')],
        loadComponent: () =>
          import('./features/supervision/fleet/fleet-page').then((m) => m.FleetPage),
      },
      {
        path: 'supervision/incidents',
        title: 'Incidents',
        data: { fullBleed: true },
        canActivate: [staffRoleGuard('dispatcher')],
        loadComponent: () =>
          import('./features/supervision/incidents/incidents-page').then((m) => m.IncidentsPage),
      },
      {
        path: 'supervision/commandes',
        title: 'Commandes',
        data: { fullBleed: true },
        canActivate: [staffRoleGuard('dispatcher')],
        loadComponent: () =>
          import('./features/supervision/orders/orders-page').then((m) => m.OrdersPage),
      },
      {
        path: 'supervision/dashboard',
        title: 'Dashboard',
        canActivate: [staffRoleGuard('dispatcher')],
        loadComponent: () =>
          import('./features/supervision/dashboard/dashboard-page').then((m) => m.DashboardPage),
      },
      {
        path: 'administration/livreurs',
        title: 'Livreurs',
        // dispatcher en lecture (décision C1) — les actions sont gatées dans l'écran
        canActivate: [staffRoleGuard('dispatcher')],
        loadComponent: () =>
          import('./features/administration/drivers/drivers-page').then((m) => m.DriversPage),
      },
      {
        path: 'administration/livreurs/:id',
        title: 'Revue KYC',
        canActivate: [staffRoleGuard('dispatcher')],
        loadComponent: () =>
          import('./features/administration/drivers/driver-account-page').then(
            (m) => m.DriverAccountPage,
          ),
      },
      {
        path: 'administration/livreurs/:id/courses',
        title: 'Courses du livreur',
        canActivate: [staffRoleGuard('dispatcher')],
        loadComponent: () =>
          import('./features/administration/drivers/driver-deliveries-page').then(
            (m) => m.DriverDeliveriesPage,
          ),
      },
      {
        path: 'administration/livreurs/:id/wallet',
        title: 'Wallet livreur',
        canActivate: [staffRoleGuard('dispatcher')],
        loadComponent: () =>
          import('./features/administration/drivers/driver-wallet-page').then(
            (m) => m.DriverWalletPage,
          ),
      },
      {
        path: 'administration/utilisateurs',
        title: 'Utilisateurs',
        // admin uniquement (décision D1, matrice RBAC umbrella §2.2)
        canActivate: [staffRoleGuard('admin')],
        loadComponent: () =>
          import('./features/administration/users/users-page').then((m) => m.UsersPage),
      },
      {
        path: 'administration/utilisateurs/:id',
        title: 'Fiche utilisateur',
        canActivate: [staffRoleGuard('admin')],
        loadComponent: () =>
          import('./features/administration/users/user-detail-page').then((m) => m.UserDetailPage),
      },
      {
        path: 'administration/business',
        title: 'Business',
        canActivate: [staffRoleGuard('admin')],
        loadComponent: () =>
          import('./features/administration/business/business-page').then((m) => m.BusinessPage),
      },
      {
        path: 'administration/business/:id',
        title: 'Fiche établissement',
        canActivate: [staffRoleGuard('admin')],
        loadComponent: () =>
          import('./features/administration/business/business-detail-page').then(
            (m) => m.BusinessDetailPage,
          ),
      },
      // Référentiels marketplace (feature F) — admin only
      {
        path: 'administration/zones',
        title: 'Zones de livraison',
        canActivate: [staffRoleGuard('admin')],
        loadComponent: () =>
          import('./features/administration/referentiels/zones-page').then((m) => m.ZonesPage),
      },
      {
        path: 'administration/categories',
        title: 'Catégories',
        canActivate: [staffRoleGuard('admin')],
        loadComponent: () =>
          import('./features/administration/referentiels/categories-page').then(
            (m) => m.CategoriesPage,
          ),
      },
      {
        path: 'administration/verticales',
        title: 'Verticales',
        canActivate: [staffRoleGuard('admin')],
        loadComponent: () =>
          import('./features/administration/referentiels/verticals-page').then(
            (m) => m.VerticalsPage,
          ),
      },
      {
        path: 'administration/promos',
        title: 'Codes promo',
        canActivate: [staffRoleGuard('admin')],
        loadComponent: () =>
          import('./features/administration/promos/promos-page').then((m) => m.PromosPage),
      },
      {
        path: 'commercial/restaurants',
        title: 'Restaurants',
        canActivate: [staffRoleGuard('sales')],
        loadComponent: () =>
          import('./features/commercial/restaurants/restaurants-page').then(
            (m) => m.RestaurantsPage,
          ),
      },
      {
        path: 'commercial/restaurants/:id/fiche',
        title: 'Fiche restaurant',
        canActivate: [staffRoleGuard('sales')],
        loadComponent: () =>
          import('./features/commercial/restaurants/store-page').then((m) => m.StorePage),
      },
      {
        path: 'commercial/restaurants/:id',
        title: 'Assistant menu',
        canActivate: [staffRoleGuard('sales')],
        loadComponent: () =>
          import('./features/commercial/restaurants/menu-page').then((m) => m.MenuPage),
      },
      {
        path: 'commercial/pipeline',
        title: 'Pipeline',
        canActivate: [staffRoleGuard('sales')],
        loadComponent: () =>
          import('./features/commercial/pipeline/pipeline-page').then((m) => m.PipelinePage),
      },
      {
        path: 'commercial/pipeline/:id',
        title: 'Fiche lead',
        canActivate: [staffRoleGuard('sales')],
        loadComponent: () =>
          import('./features/commercial/pipeline/lead-page').then((m) => m.LeadPage),
      },
      {
        path: 'reglages',
        title: 'Réglages',
        loadComponent: () =>
          import('./features/settings/settings-page').then((m) => m.SettingsPage),
      },
    ],
  },
  { path: '**', redirectTo: '' },
];
